import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getThreads, getEmployees } from "../../../api/serviceRecordApi";

const ServiceRecordList = () => {
    const [threads, setThreads] = useState([]);
    const { user } = useSelector((state) => state.auth);
    const navigate = useNavigate();
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const isAdmin = user?.user?.LevelId === 1;

    useEffect(() => {
        fetchThreads();
        if (isAdmin) fetchEmployees();
    }, [isAdmin]);

    const fetchThreads = async () => {
        try {
            const res = await getThreads();
            setThreads(res.data.data || []);
        } catch (err) {
            console.error("Error fetching threads:", err);
        }
    };

    const fetchEmployees = async () => {
        try {
            const res = await getEmployees();
            setEmployees(res.data.data || []);
        } catch (err) {
            console.error("Error fetching employees:", err);
        }
    };

    const getStatusBadge = (status) => {
        if (status === "Open") {
            return <span className="badge bg-success px-3 py-2">Open</span>;
        }
        return <span className="badge bg-secondary px-3 py-2">Closed</span>;
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";

        return new Date(dateString).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            timeZone: "UTC"
        });
    };

    const filteredThreads = threads.filter(thread =>
        (!selectedEmployee || String(thread.EmployeeID) === String(selectedEmployee)) &&
        (!selectedStatus || thread.Status === selectedStatus)
    );

    const openCount = threads.filter(t => t.Status === "Open").length;
    const closedCount = threads.filter(t => t.Status === "Closed").length;

    return (
        <div className="container-fluid admin-module-page admin-service-record-page py-4">
            <div className="admin-page-header d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                <div>
                    <span className="admin-page-kicker">Service Record</span>
                    <h3 className="admin-page-title fw-bold mb-1">Employee Service Records</h3>
                    <p className="admin-page-description mb-0">
                        Manage employee service history, career progression, and official employment records.
                    </p>
                </div>
                {isAdmin && (
                    <button
                        className="btn btn-primary admin-page-action"
                        onClick={() => navigate("/service-record/new")}
                    >
                        + Initiate new thread
                    </button>
                )}
            </div>

            <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="admin-chip">Open Threads: {openCount}</span>
                <span className="admin-chip">Closed Threads: {closedCount}</span>
            </div>

            {isAdmin && (
                <div className="card admin-surface-card admin-filter-card mb-4 sticky-top">
                    <div className="card-body admin-surface-body">
                        <div className="row g-3 align-items-end">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">Filter by Employee</label>
                                <select
                                    className="form-select"
                                    value={selectedEmployee}
                                    onChange={(e) => setSelectedEmployee(e.target.value)}
                                >
                                    <option value="">All Employees</option>
                                    {employees.map(emp => (
                                        <option key={emp.intUserId} value={emp.intUserId}>
                                            {emp.vchFullName}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="col-md-3">
                                <label className="form-label fw-semibold">Filter by Status</label>
                                <select
                                    className="form-select"
                                    value={selectedStatus}
                                    onChange={(e) => setSelectedStatus(e.target.value)}
                                >
                                    <option value="">All Status</option>
                                    <option value="Open">Open</option>
                                    <option value="Closed">Closed</option>
                                </select>
                            </div>

                            <div className="col-md-2 d-grid">
                                <button
                                    className="btn btn-outline-secondary"
                                    disabled={!selectedEmployee && !selectedStatus}
                                    onClick={() => {
                                        setSelectedEmployee("");
                                        setSelectedStatus("");
                                    }}
                                >
                                    Clear Filters
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {filteredThreads.length === 0 ? (
                <div className="card admin-surface-card text-center py-5">
                    <div className="card-body">
                        <i className="bi bi-inbox fs-1 text-muted d-block mb-3"></i>
                        <p className="text-muted mb-0">No service records found.</p>
                    </div>
                </div>
            ) : (
                <div className="row g-3">
                    {filteredThreads.map((thread) => (
                        <div key={thread.ThreadID} className="col-12">
                            <div
                                className="card admin-list-card border-0 service-record-card"
                                role="button"
                                tabIndex={0}
                                onClick={() => navigate(`/service-record/${thread.ThreadID}`)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        navigate(`/service-record/${thread.ThreadID}`);
                                    }
                                }}
                            >
                                <div className="card-body p-3 p-lg-4">
                                    <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                                        <div className="flex-grow-1">
                                            <h6 className="fw-semibold mb-1">
                                                #{thread.ThreadID} - {thread.Subject}
                                            </h6>
                                            <div className="small text-muted mb-1">
                                                <span className="me-3">
                                                    <strong>Employee:</strong> {thread.EmployeeName || "-"}
                                                </span>
                                                <span>
                                                    <strong>Authorized By:</strong> {thread.AuthorizedBy}
                                                </span>
                                            </div>
                                            <div className="small text-muted">
                                                Last activity: {formatDateTime(thread.LastActivityDate || thread.CreatedDate)}
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-start align-items-lg-center gap-2 flex-wrap">
                                            {getStatusBadge(thread.Status)}
                                            <span className="btn btn-outline-primary btn-sm">
                                                View Thread
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ServiceRecordList;
